import React, { useEffect, useState } from 'react';
import { Check } from 'lucide-react';

/**
 * SuccessOverlay component with confetti animation
 * Automatically removes itself from DOM after animation completes
 * @param {function} onComplete - Callback when animation completes and component unmounts
 * @param {number} duration - Duration in milliseconds (default: 3000)
 * @param {string} message - Success message to display (default: 'Success!')
 */
const SuccessOverlay = ({ onComplete, duration = 3000, message = 'Success!' }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [confetti, setConfetti] = useState([]);

  useEffect(() => {
    // Generate confetti pieces
    const pieces = Array.from({ length: 150 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.8,
      duration: 2 + Math.random() * 1.5,
      rotation: Math.random() * 360,
      size: 8 + Math.random() * 8,
      color: [
        '#3b82f6',
        '#ef4444',
        '#10b981',
        '#f59e0b',
        '#8b5cf6',
        '#ec4899',
        '#06b6d4',
        '#f43f5e',
      ][Math.floor(Math.random() * 8)],
    }));
    setConfetti(pieces);

    // Start fade out before complete removal
    const fadeTimer = setTimeout(() => {
      setIsVisible(false);
    }, duration - 500);

    // Remove from DOM and call onComplete
    const completeTimer = setTimeout(() => {
      if (onComplete) onComplete();
    }, duration);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
    };
  }, [duration, onComplete]);

  if (!isVisible && confetti.length === 0) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-opacity duration-500 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ pointerEvents: isVisible ? 'auto' : 'none' }}
    >
      {/* Confetti pieces */}
      {confetti.map((piece) => (
        <div
          key={piece.id}
          className='absolute top-0 rounded-sm animate-confetti'
          style={{
            left: `${piece.left}%`,
            width: `${piece.size}px`,
            height: `${piece.size}px`,
            backgroundColor: piece.color,
            animationDelay: `${piece.delay}s`,
            animationDuration: `${piece.duration}s`,
            transform: `rotate(${piece.rotation}deg)`,
          }}
        />
      ))}

      {/* Success message */}
      <div
        className={`relative z-10 transform transition-all duration-700 ${
          isVisible ? 'scale-100 opacity-100' : 'scale-150 opacity-0'
        }`}
      >
        <div className='bg-white rounded-2xl shadow-2xl p-8 text-center'>
          {/* Checkmark circle */}
          <div className='mx-auto w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mb-4 animate-scale-in'>
            <Check className='w-12 h-12 text-white' strokeWidth={3} />
          </div>

          {/* Success text */}
          <h2 className='text-3xl font-bold text-gray-900 mb-2'>{message}</h2>
        </div>
      </div>
    </div>
  );
};

export default SuccessOverlay;
